import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import downloadApp from "../assets/AppDownload.png";
import verifyOtp from "../assets/OTPVerify.png";
import { FaExclamationTriangle } from "react-icons/fa";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import openNotification from "components/utils/CustomNotification";
import { fetchTwoFaQrCode, verifySetup2Fa } from "components/API/ApiEnpoints";

const TwoFa: React.FC = () => {
  const topRef = useRef<HTMLDivElement | null>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const [otp, setOtp] = useState<string>("");
  const [qrCode, setQrCode] = useState<string>("");
  const [secretKey, setSecretKey] = useState<string>("");
  const [showSecretKey, setShowSecretKey] = useState<boolean>(false);
  const [retry, setRetry] = useState<boolean>(false);
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const getTwoFaQrCode = async () => {
    const apiResponse = await fetchTwoFaQrCode(sessionUid, userUid);
    if (Object.keys(apiResponse).length > 0) {
      if (apiResponse.status === "Success") {
        setQrCode(`data:image/png;base64,${apiResponse.data.qr_code}`);
        setSecretKey(apiResponse.data.two_fa_auth_key);
      } else {
        openNotification({
          type: "error",
          message: apiResponse.data || apiResponse.message,
        });
      }
      SessionExtend();
    } else {
      openNotification({
        type: "error",
        message: "Failed to generate auth qr code",
      });
    }
  };

  const verifyQrCode = async () => {
    setRetry(false);
    const apiResponse = await verifySetup2Fa(sessionUid, userUid, otp);
    if (Object.keys(apiResponse).length > 0) {
      if (apiResponse.status === "Success") {
        openNotification({
          type: "success",
          message: apiResponse.data || apiResponse.message,
        });
        setTimeout(() => {
          localStorage.clear();
          navigate("/");
        }, 2000);
      } else {
        setRetry(true);
        setOtp("");
        openNotification({
          type: "error",
          message: apiResponse.data || apiResponse.message,
        });
        getTwoFaQrCode();
      }
      SessionExtend();
    } else {
      openNotification({
        type: "error",
        message: "Failed to verity OTP",
      });
    }
  };

  useEffect(() => {
    getTwoFaQrCode();
  }, []);

  useEffect(() => {
    if (retry && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [retry]);

  return (
    <TwoFaMainContainer>
      <div className="twofa-block-flex-1">
        <h3>Configure Two-Factor Authentication</h3>
        <HeaderLine />
      </div>
      <div ref={topRef} className="twofa-block-flex-1-2">
        <span>To Enable two-factor authentication follow the below steps:</span>
        <div className="twofa-block-flex-1-2-1">
          <div className="twofa-block-flex-1-2-1-1">
            <img
              className="twofa-download-image"
              src={downloadApp}
              alt="Download App"
            />
          </div>
          <div className="twofa-block-flex-1-2-1-2">
            <span>
              1. Download any authenticator app (Microsoft Authenticator, Google
              Authenticator, etc.) from play store/app store
            </span>
          </div>
        </div>
        <div className="twofa-block-flex-1-2-2">
          <div className="twofa-block-flex-1-2-2-1">
            <img src={qrCode} alt="QR Code" className="qr-code" />
          </div>
          <div className="twofa-block-flex-1-2-2-2">
            <span>2. Scan the QR Code in your authenticator app</span>
            <div className="twofa-key-container">
              <span>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowSecretKey(!showSecretKey);
                  }}
                >
                  Can't scan this QR Code?
                </a>
              </span>
              {showSecretKey && (
                <>
                  <span>
                    Use the following key in your authentication app's manual
                    entry:
                  </span>
                  <span className="secret-key">{secretKey}</span>
                  <span>Then enter the 6-digit verification code below.</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="twofa-block-flex-1-2-3">
          <div className="twofa-block-flex-1-2-3-1">
            <img
              className="twofa-verify-opt-image"
              src={verifyOtp}
              alt="Verify OTP"
            />
          </div>
          <div className="twofa-block-flex-1-2-3-2">
            <div className="twofa-block-flex-1-2-3-2-1">
              <span>
                3. Once the QR code above is scanned, enter the 6-digit
                verification code generated by the app.
              </span>
              <input
                className="twofa-block-flex-1-2-3-input"
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setOtp(value);
                }}
                placeholder="Enter Code"
                value={otp}
                maxLength={6}
              />
              <button disabled={!otp} onClick={verifyQrCode}>
                Verify Code and Activate
              </button>
            </div>
            <div className="twofa-note-block">
              <div ref={alertRef} style={{ marginTop: "0.5rem" }}>
                {retry && (
                  <span style={{ color: "red" }}>
                    Your verification code is incorrect or expired. Please delete the existing QR code in your authenticator app and try again.
                  </span>
                )}
              </div>
              <div>
                <FaExclamationTriangle className="note-icon" />
                <span>
                  Note: Enabling two-factor authentication will log out all
                  current user's sessions from all devices.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </TwoFaMainContainer>
  );
};

export default TwoFa;

// --- STYLES ---
const TwoFaMainContainer = styled.div`
  margin: 0 2%;
  padding: 0 0 2% 0;

  .twofa-block-flex-1 h3 {
    font-size: 1.2rem;
  }

  .twofa-block-flex-1-2 {
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
    line-height: 1.5rem;
    gap: 0.5rem;
  }

  .twofa-download-image {
    width: 150px;
    height: 150px;
  }

  .twofa-block-flex-1-2-1,
  .twofa-block-flex-1-2-2 {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    padding: 1% 0;
    border-bottom: 1px solid #ddd;
  }

  .qr-code {
    width: 150px;
    height: 150px;
  }

  .twofa-key-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .secret-key {
    font-family: monospace;
    font-size: 1rem;
    background-color: #f3f3f3;
    padding: 0.5rem;
    border-radius: 0.5rem;
    word-break: break-word;
  }

  .twofa-block-flex-1-2-3 {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    padding: 1% 0;
    border-bottom: 1px solid #ddd;

    button {
      background-color: #094752;
      color: white;
      padding: 0.5em;
      border-radius: 0.5rem;
      border: none;
      cursor: pointer;
      font-size: 0.875rem;
      font-weight: 600;

      &:hover {
        background-color: rgb(15, 98, 112);
      }

      &:disabled {
        background-color: #ccc;
      }
    }
  }

  .twofa-verify-opt-image {
    width: 150px;
    height: 150px;
  }

  .twofa-block-flex-1-2-3-input {
    padding: 8px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 0.75rem;
  }

  .twofa-block-flex-1-2-3-2 {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .twofa-block-flex-1-2-3-2-1 {
    display: flex;
    gap: 1rem;
  }

  .twofa-note-block {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 1rem;
    font-size: 0.9rem;
    color: #555;
    flex-wrap: wrap;

    .note-icon {
      color: #e6a000;
      font-size: 1.2rem;
      padding-right: 1rem;
    }

    @media (max-width: 600px) {
      flex-direction: column;
      padding: 0.75rem;
    }
  }

  @media (max-width: 768px) {
    .twofa-block-flex-1-2 {
      padding: 1rem;
    }

    .twofa-block-flex-1-2-1,
    .twofa-block-flex-1-2-2,
    .twofa-block-flex-1-2-3 {
      flex-direction: column;
    }

    .twofa-block-flex-1-2-3-2-1 {
      flex-direction: column;
    }
  }

  @media (max-width: 480px) {
    .qr-code {
      width: 140px;
      height: 140px;
    }
  }
`;

const HeaderLine = styled.div`
  width: 100%;
  height: 2px;
  background-color: #094752;
  margin-bottom: 18px;
`;
